import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';

/**
 * Trip Navigation Cell Renderer
 *
 * Displays trip ID with clickable link that opens trip monitoring screen in new tab.
 * Shows rocket icon when trip exists and displays tooltip on hover.
 * Opens /monitor/principal-screen?tripId={id} in new tab when clicked.
 *
 * Features:
 * - Clickable trip ID with underline hover effect
 * - Rocket icon animation on hover
 * - Opens in new tab (target="_blank")
 * - Tooltip: "Ir al viaje"
 * - Shows "-" when no trip exists
 *
 * @example
 * ```typescript
 * {
 *   field: 'trip.id',
 *   headerName: 'Viaje',
 *   cellRenderer: TripNavigationCellRendererComponent,
 *   minWidth: 120,
 *   sortable: true,
 *   pinned: 'left',
 * }
 * ```
 */
@Component({
  selector: "app-trip-navigation-cell-renderer",
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  template: `
    <div class="flex items-center">
      <span
        *ngIf="tripId; else noTrip"
        class="flex items-center cursor-pointer hover:underline transition-all"
        (click)="navigateToTrip()"
        [matTooltip]="'Ir al viaje'"
        matTooltipPosition="above">
        {{ tripId }}
        <mat-icon
          class="ml-2 icon-size-4 hover:animate-bounce"
          svgIcon="heroicons_outline:rocket-launch">
        </mat-icon>
      </span>
      <ng-template #noTrip>
        <span class="text-gray-400">-</span>
      </ng-template>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-4px);
      }
    }

    .hover\\:animate-bounce:hover {
      animation: bounce 0.6s ease-in-out;
    }
  `]
})
export class TripNavigationCellRendererComponent implements CellRenderer<any> {
  @Input() params!: CellRendererParams<any>;

  private readonly router = inject(Router);

  get tripId(): string | undefined {
    const trip = this.params?.data?.trip;
    return trip?.id || trip?._id;
  }

  navigateToTrip(): void {
    if (!this.tripId) return;

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/monitor/principal-screen'], {
        queryParams: { tripId: this.tripId }
      })
    );

    window.open(url, '_blank');
  }
}
